/**
 * dislocationField — animated crystallographic defects.
 *
 * Real crystals aren't perfect lattices — they have grain
 * boundaries (where two crystal grains meet at different
 * orientations) and dislocations (point defects where the
 * lattice is sheared by one Burgers vector). This shader
 * renders a polycrystal with 36 grain seeds, each with its
 * own lattice orientation, and dislocation cores at the
 * seed points.
 *
 * Visual layers:
 *   1. Voronoi cell boundaries (sharp bright lines)
 *   2. Per-grain lattice orientation (anisotropic
 *      birefringence texture — the "color" of each grain
 *      depends on the angle to the pixel's radial vector)
 *   3. Dislocation cores: bright dots at each seed with a
 *      shockwave ring that periodically expands
 *   4. Topological charge: +1 cores have a "rose" pattern,
 *      -1 cores have a "vortex" — the user can SEE the
 *      topological charge by looking carefully
 *   5. Grain-boundary breath: a faint pulse along the
 *      boundary that follows the audio
 *   6. Acoustic background: faint weave of dark moiré
 *      bands that move with the slow grain rotation
 *
 * Reference: Hirth & Lothe "Theory of Dislocations";
 * Hull & Bacon "Introduction to Dislocations".
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface DislocationFieldPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}
const VERT_SRC = `#version 300 es
precision highp float; layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
float bass; float mid; float high;

const float TAU = 6.283185307179586;
const float PI = 3.14159265358979;

float hash11(float n) { return fract(sin(n * 12.9898) * 43758.5453); }
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// === Grain seeds ===
// 36 grains in a slowly-stirring distribution. Each has a
// position, an orientation angle, and a topological charge
// (the Burgers vector's sign).
const int N_GRAINS = 36;

void grain(int i, float t, out vec2 pos, out float angle, out float charge) {
  float fi = float(i);
  // Halton-like quasi-uniform distribution.
  float ph = fi * 2.39996;
  float rad = sqrt(fi / float(N_GRAINS)) * 1.2;
  pos = vec2(cos(ph), sin(ph)) * rad;
  // Slow precession.
  float pre = t * 0.04;
  pos = vec2(pos.x * cos(pre) - pos.y * sin(pre), pos.x * sin(pre) + pos.y * cos(pre));
  // Lattice orientation angle.
  angle = hash11(fi * 5.41) * TAU + t * (0.04 + 0.03 * hash11(fi * 7.93));
  // Topological charge: ±1 or 0.
  float mod3 = mod(fi, 3.0);
  charge = (mod3 < 0.5) ? 1.0 : (mod3 < 1.5 ? -1.0 : 0.0);
}

vec3 iridescent(float t) {
  return 0.5 + 0.5 * cos(6.28318530718 * (t + vec3(0.0, 0.33, 0.67)));
}

void main() {
  vec2 uv = (vUV - 0.5) * 2.0;
  uv.x *= uResolution.x / uResolution.y;

  float T = uTime;

  // === Find the two nearest grains ===
  float minD = 1e3;
  float minD2 = 1e3;
  int idx = 0;
  int idx2 = 0;
  vec2 closestPos = vec2(0.0);
  float closestAngle = 0.0;
  float closestCharge = 0.0;

  for (int i = 0; i < N_GRAINS; i++) {
    vec2 gpos;
    float gangle;
    float gcharge;
    grain(i, T, gpos, gangle, gcharge);
    float d = distance(uv, gpos);
    if (d < minD) {
      minD2 = minD;
      idx2 = idx;
      minD = d;
      idx = i;
      closestPos = gpos;
      closestAngle = gangle;
      closestCharge = gcharge;
    } else if (d < minD2) {
      minD2 = d;
      idx2 = i;
    }
  }

  // Get the second grain's properties for boundary blending.
  vec2 grain2Pos;
  float grain2Angle;
  float grain2Charge;
  grain(idx2, T, grain2Pos, grain2Angle, grain2Charge);

  // === Grain-boundary distance ===
  // The perpendicular bisector distance: (minD2 - minD) / 2.
  float gbDist = (minD2 - minD) * 0.5;

  // === Lattice direction at this pixel ===
  vec2 latDir = vec2(cos(closestAngle), sin(closestAngle));
  vec2 gridP = uv - closestPos;
  float along = dot(gridP, latDir);
  float across = dot(gridP, vec2(-latDir.y, latDir.x));

  // === Birefringence texture: color depends on local lattice
  // direction projected onto the radial vector from the grain.
  // Real crystals show interference colors when light passes
  // through — we approximate this by modulating color along
  // the lattice direction.
  float phase = along * 18.0 - T * 1.5;
  float latticeIntensity = exp(-across * across * 8.0);
  float latticeWave = 0.5 + 0.5 * sin(phase);
  // Grain-specific hue.
  vec3 grainHue = iridescent(float(idx) * 0.137 + T * 0.04);
  vec3 latCol = grainHue * latticeWave * latticeIntensity;

  // === Color: blend in the birefringence texture, but only deeply
  // inside the grain (away from the boundary).
  float inside = smoothstep(0.04, 0.10, gbDist);
  vec3 col = latCol * inside;

  // === Grain boundary: bright thin line where two grains meet.
  float gbLine = exp(-gbDist * 80.0) * 1.5;
  // Boundary color: white or slightly tinted by the average of
  // the two grains' hues.
  vec3 gbColor = mix(grainHue, iridescent(float(idx2) * 0.137 + T * 0.04), 0.5);
  col += vec3(1.0) * gbLine * 0.9;
  col += gbColor * gbLine * 0.3;

  // === Triple points: where three grains meet ===
  // (Approximated by finding where minD is close to minD2 AND
  // there's a third grain nearby.)
  // Cheap proxy: amplify the boundary where the angular difference
  // between the two boundary-defining grains is high.
  float angleDiff = abs(closestAngle - grain2Angle);
  angleDiff = min(angleDiff, TAU - angleDiff);
  float triplePoint = smoothstep(0.5, 1.0, angleDiff) * exp(-gbDist * 30.0);
  col += vec3(0.95, 0.85, 0.65) * triplePoint * 0.8;

  // === Dislocation cores: bright dots at each grain center ===
  // The core has a fixed bright glow, plus a SHOCKWAVE RING that
  // periodically expands outward (10-second period).
  float shockwave = 0.0;
  float coreD = minD;
  float coreGlow = exp(-coreD * coreD * 1500.0) * 1.5;
  // Shockwave: a thin ring expanding from r=0 outward.
  float shockPhase = mod(T * 0.1 + float(idx) * 0.13, 1.0);
  float shockR = shockPhase * 0.4;
  float shockRing = exp(-pow(coreD - shockR, 2.0) * 800.0) * (1.0 - shockPhase);
  shockwave = shockRing * 1.2;
  // Topological charge: +1 = "rose" (radial spokes), -1 = "vortex"
  // (tangential streaks), 0 = dot.
  vec3 coreCol = vec3(1.0, 0.7, 0.4);
  if (abs(closestCharge) > 0.5) {
    float arg = atan(uv.y - closestPos.y, uv.x - closestPos.x + 1e-6);
    if (closestCharge > 0.5) {
      // Rose: radial spokes.
      float rose = 0.5 + 0.5 * cos(arg * 5.0 - T * 0.7);
      coreCol = mix(vec3(1.0, 0.5, 0.3), vec3(0.95, 0.85, 0.65), rose);
    } else {
      // Vortex: tangential streaks.
      float vortex = 0.5 + 0.5 * sin(arg * 3.0 + coreD * 30.0 - T * 1.0);
      coreCol = mix(vec3(0.3, 0.5, 1.0), vec3(0.6, 0.9, 0.95), vortex);
    }
  }
  col += coreCol * coreGlow;
  col += vec3(1.0, 0.95, 0.85) * shockwave;

  // === Drive all cores: render ALL 36 dislocation cores, not just
  // the closest one. Each has a faint glow plus its shockwave.
  for (int i = 0; i < N_GRAINS; i++) {
    vec2 gpos;
    float gangle;
    float gcharge;
    grain(i, T, gpos, gangle, gcharge);
    float d = length(uv - gpos);
    if (d < 0.01) {
      float fcore = exp(-d * d * 10000.0);
      col += vec3(1.0, 0.95, 0.85) * fcore * 0.5;
    }
    // Shockwave from each non-closest grain.
    if (i != idx) {
      float sp = mod(T * 0.1 + float(i) * 0.13, 1.0);
      float sr = sp * 0.4;
      float sring = exp(-pow(d - sr, 2.0) * 800.0) * (1.0 - sp) * 0.3;
      col += vec3(0.95, 0.85, 0.55) * sring;
    }
  }

  // === Substrate: a deep blue background with a faint moiré pattern ===
  vec3 substrate = vec3(0.04, 0.06, 0.10);
  // Very subtle weave: two superimposed sine fields at different angles.
  float weave1 = 0.5 + 0.5 * sin(uv.x * 200.0 + T * 0.3);
  float weave2 = 0.5 + 0.5 * sin(uv.y * 200.0 - T * 0.3);
  float weave = mix(weave1, weave2, 0.5);
  substrate += vec3(0.01, 0.02, 0.04) * weave;

  // Blend substrate with the lattice texture by opacity.
  float latticeOpacity = 0.3 + 0.7 * inside;
  col = mix(substrate, col, latticeOpacity);

  // === Bass: pulse the shockwave a bit harder ===
  col *= 0.9 + 0.1 * bass;

  // === Vignette ===
  float vig = 1.0 - dot(uv, uv) * 0.25;
  col *= clamp(vig, 0.0, 1.0);

  // === Tone mapping + gamma ===
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}`;

function bands(t: number, o: Float32Array): void {
  for (let i = 0; i < 9; i++) o[i] = 0.5 + 0.3 * Math.sin(t * (0.8 + i * 0.17));
}
export function computeDislocationFieldBands(t: number, o: Float32Array): void { bands(t, o); }
export function computeDislocationFieldRealtimeBands(f: Uint8Array, o: Float32Array): void {
  const N = f.length;
  for (let i = 0; i < 9; i++) {
    const start = Math.floor(i / 9 * N);
    const end = Math.floor((i + 1) / 9 * N);
    let s = 0, n = 0;
    for (let k = start; k < end; k++) { s += f[k] || 0; n++; }
    o[i] = n > 0 ? (s / n) / 255 : 0;
  }
}
export function createDislocationFieldPipeline(gl: WebGL2RenderingContext): DislocationFieldPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return { program, vao, vbo, bands: new Float32Array(9) };
}
export function drawDislocationField(gl: WebGL2RenderingContext, p: DislocationFieldPipeline, w: number, h: number, t: number): void {
  gl.useProgram(p.program);
  const prog = p.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), w, h);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), p.bands[0], p.bands[1], p.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), p.bands[3], p.bands[4], p.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), p.bands[6], p.bands[7], p.bands[8]);
  gl.bindVertexArray(p.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
export { VERT_SRC, FRAG_SRC };
